import { ObjectDetailsDialogState } from '~/components/Properties/ObjectDetailsDialog';
import ObjectInfoButton from '~/components/Properties/ObjectInfoButton';
import { PropertiesActions } from '~/components/Properties/PropertiesPanel';
import StructureSetProperties from '~/components/Properties/StructureSetProperties';
import StructureSetReviewStatus from '~/components/Structures/StructureSetReviewStatus';
import { AvailableStructureSetView } from '~/domain/projections/views';
import { useLiveStructureSet } from '~/hooks/domain/useLiveStructureSet';

interface StructureSetInfoButtonProps {
    structureSet: AvailableStructureSetView;
    /** The dialog state, when the row holds it; otherwise the button's own. */
    dialog?: ObjectDetailsDialogState;
    onDelete?: () => void;
    'data-testid'?: string;
}

/**
 * The (i) that opens a structure set's details dialog: its name and its
 * approval status in the header, its parsed DICOM on the tags tab when the
 * set is loaded, and StructureSetProperties as the body.
 */
export default function StructureSetInfoButton({
    structureSet,
    dialog,
    onDelete,
    'data-testid': testId,
}: StructureSetInfoButtonProps) {
    // Only the loaded set has parsed DICOM behind it; a session-only set
    // has none, and the details dialog then omits its DICOM tags tab
    // rather than offering an empty one.
    const liveStructureSet = useLiveStructureSet(structureSet.structureSetId);

    return (
        <ObjectInfoButton
            titleId="structureSet"
            name={structureSet.displayName}
            /*
             * The status reads itself inside the dialog. Read here, every
             * row in a list would subscribe to the projection for a badge
             * that only renders once a row is opened.
             */
            badges={
                <StructureSetReviewStatus id={structureSet.structureSetId} />
            }
            actions={<PropertiesActions onDelete={onDelete} />}
            dataset={liveStructureSet?.dicomStructureSet.datasetWrapper}
            dialog={dialog}
            data-testid={testId}
        >
            <StructureSetProperties structureSet={structureSet} />
        </ObjectInfoButton>
    );
}
